import { DEFAULT_ATTRIBUTES, ONE_TO_ONE, UID } from 'shared/api/query/constants';
import {
  accessPolicy,
  accessPolicyScope,
  account as accountConfig,
  property as propertyConfig,
  propertyOption as propertyOptionConfig,
  propertySettings as propertySettingsConfig,
  propertyValue,
  space as spaceConfig,
  storageObject,
  user,
  userScopeTree,
} from 'shared/api/query/configs.json';
import { propertyVisibility } from 'shared/constants.json';
import { userScopeTreeChildren } from 'shared/api/query/user-scope';

export const reverseEdge = (edge) => `~${edge}`;

const storageObjectEdges = [
  { attr: UID },
  { attr: storageObject.edges.getURL },
  { attr: storageObject.edges.updateURL },
  { attr: storageObject.edges.blob },
];

export const userEdgesSlim = [
  { attr: UID },
  { attr: user.edges.firstName, default: '' },
  { attr: user.edges.lastName, default: '' },
  { attr: user.edges.email, default: '' },
  { attr: user.edges.isDemo, default: false },
  {
    attr: user.edges.profileImage,
    default: null,
    model: storageObject.model,
    children: storageObjectEdges,
  },
];

const propertySettingsEdges = [
  ...DEFAULT_ATTRIBUTES,
  { attr: propertySettingsConfig.edges.visibility, default: propertyVisibility.alwaysVisible },
  { attr: propertySettingsConfig.edges.property, model: propertyConfig.model, children: [{ attr: UID }] },
  { attr: propertySettingsConfig.edges.propertyOption, model: propertyOptionConfig.model, children: [{ attr: UID }] },
];

const propertyOptionEdges = [
  ...DEFAULT_ATTRIBUTES,
  { attr: propertyOptionConfig.edges.archivedAt, default: null },
  { attr: propertyOptionConfig.edges.label, default: { de: '' } },
  { attr: propertyOptionConfig.edges.icon, default: '' },
  { attr: propertyOptionConfig.edges.color, default: null },
  { attr: propertyOptionConfig.edges.isNotDeletable, default: false },
  { attr: propertyOptionConfig.edges.isObjective, default: false },
  { attr: propertyOptionConfig.edges.isKeyResult, default: false },
  { attr: propertyOptionConfig.edges.goalProgressVisibility, default: propertyVisibility.alwaysVisible },
  { attr: propertyOptionConfig.edges.goalCycleVisibility, default: propertyVisibility.alwaysVisible },
  { attr: propertyOptionConfig.edges.goalParentVisibility, default: propertyVisibility.alwaysVisible },
  { attr: propertyOptionConfig.edges.descriptionVisibility, default: propertyVisibility.alwaysVisible },
  { attr: propertyOptionConfig.edges.descriptionRequired, default: false },
  { attr: propertyOptionConfig.edges.cycleRequired, default: false },
  { attr: propertyOptionConfig.edges.parentRequired, default: false },
  { attr: propertyOptionConfig.edges.score, default: null },
  {
    attr: propertyOptionConfig.edges.requiredProperties,
    model: propertyConfig.model,
    default: [],
    children: [{ attr: UID }],
  },
  {
    attr: propertyOptionConfig.edges.allowedMeasurementTypes,
    default: [],
  },
  {
    attr: propertyOptionConfig.edges.canBeChildOf,
    model: propertyOptionConfig.model,
    default: [],
    children: [{ attr: UID }],
  },
  {
    attr: reverseEdge(propertyOptionConfig.edges.parents),
    alias: 'children',
    model: propertyOptionConfig.model,
    children: [{ attr: UID }],
    default: [],
  },
  {
    attr: propertyOptionConfig.edges.property,
    model: propertyConfig.model,
    children: [{ attr: UID }],
  },
  {
    attr: propertyOptionConfig.edges.parents,
    model: propertyOptionConfig.model,
    children: [{ attr: UID }],
    default: [],
  },
  {
    attr: reverseEdge(propertySettingsConfig.edges.propertyOption),
    alias: 'propertySettings',
    model: propertySettingsConfig.model,
    children: propertySettingsEdges,
  },
];

const spaceEdgesSlim = [
  { attr: UID },
  { attr: spaceConfig.edges.title, default: '' },
  { attr: spaceConfig.edges.icon, default: '' },
  { attr: spaceConfig.edges.color, default: null },
];

export const propertyValueEdges = [
  ...DEFAULT_ATTRIBUTES,
  { attr: propertyValue.edges.number, default: null },
  { attr: propertyValue.edges.timestamp, default: null },
  { attr: propertyValue.edges.text, default: '' },
  {
    attr: propertyValue.edges.selectedOptions,
    model: propertyOptionConfig.model,
    default: [],
    children: propertyOptionEdges,
  },
  {
    attr: propertyValue.edges.spaces,
    model: spaceConfig.model,
    default: [],
    children: spaceEdgesSlim,
  },
  {
    attr: propertyValue.edges.property,
    model: propertyConfig.model,
    assoc: ONE_TO_ONE,
    children: [
      { attr: UID },
      { attr: propertyConfig.edges.type },
      { attr: propertyConfig.edges.label },
      { attr: propertyConfig.edges.isGoalType },
      { attr: propertyConfig.edges.status },
      { attr: propertyConfig.edges.numberFormat },
    ],
  },
  {
    attr: propertyValue.edges.users,
    model: user.model,
    default: [],
    order: [{ attr: user.edges.firstName }, { attr: user.edges.lastName }],
    children: userEdgesSlim,
  },
];

const accessPolicyScopeChildren = [
  { attr: UID },
  { attr: accessPolicyScope.edges.accessType },
  { attr: accessPolicyScope.edges.userIsInScope, default: false },
  {
    attr: accessPolicyScope.edges.scope,
    model: userScopeTree.model,
    default: null,
    children: userScopeTreeChildren,
  },
  {
    attr: accessPolicyScope.edges.scopeUsers,
    model: user.model,
    default: [],
    children: [{ attr: UID }],
  },
];

export const accessPolicyChildren = [
  { attr: UID },
  { attr: accessPolicy.edges.accountAccess },
  {
    attr: accessPolicy.edges.account,
    model: accountConfig.model,
    default: null,
    children: [{ attr: UID }],
  },
  {
    attr: accessPolicy.edges.scopes,
    model: accessPolicyScope.model,
    default: [],
    children: accessPolicyScopeChildren,
  },
];
